import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../db/engine';
import { fmtMoneda as fmt$ } from '../../utils/moneda';
import { imprimirHTML } from '../../utils/imprimir';
import { CheckCircle, Users, Plus, RefreshCw, X } from 'lucide-react';

// "Mi Plan" — el master ve qué tiene contratado, cuánto usa y lo cambia él mismo.
//
// Principio (Ricardo): que el cliente sienta que queremos que le saque provecho al
// sistema, no que le queremos cobrar. Por eso cada plan se presenta por lo que SUMA, y
// todo se resuelve aquí: nunca "contacta a Corredata".
//
// Esta pantalla no cambia nada por sí sola: le pide el cambio a /api/mi-plan, que
// verifica al master y se lo pasa al portal de Corredata (el único que puede escribir el
// plan — ver `cont_config_candado`). Las reglas de cobro viven en el portal
// (corredata-admin/functions/api/mi-plan.js); lo de aquí es solo la vista previa.

const r2 = n => Math.round((Number(n) || 0) * 100) / 100;
const fechaCorta = f => { const [y, m, dd] = String(f || '').slice(0, 10).split('-'); return dd ? `${dd}/${m}/${y}` : '—'; };
const SIEMPRE = ['dashboard', 'admin'];   // nunca se gatean: no se anuncian como parte de un plan
const GB = 1024 ** 3;
// Espacio legible: en MB mientras no llegue a 1 GB (un corretaje nuevo pesa pocos MB).
const fmtEspacio = gb => gb >= 1 ? `${(Math.round(gb * 10) / 10).toLocaleString('es-PA')} GB` : `${Math.max(0, Math.round(gb * 1024)).toLocaleString('es-PA')} MB`;

async function llamar(cuerpo) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch('/api/mi-plan', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
    body: JSON.stringify(cuerpo),
  });
  const texto = await r.text();
  let data = null;
  try { data = texto ? JSON.parse(texto) : null; } catch { /* respuesta no-JSON */ }
  if (!data) throw new Error(`El servicio de planes no respondió correctamente (${r.status}).`);
  if (!r.ok || data.error) throw new Error(data.error || `No se pudo completar (${r.status}).`);
  return data;
}

export default function MiPlan({ cfg, usuarios, modulosCatalogo, onCambio }) {
  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState('');
  const [aviso, setAviso] = useState('');
  const [pendiente, setPendiente] = useState(null);   // lo que se va a confirmar
  const [busy, setBusy] = useState(false);
  const [bajando, setBajando] = useState(null);       // id del estado de cuenta que se está abriendo

  const etiqueta = k => (modulosCatalogo || []).find(m => m.key === k)?.label || k;
  const activosLocal = (usuarios || []).filter(u => u.activo && !u.es_soporte).length;

  const cargar = useCallback(async () => {
    setCargando(true); setErr('');
    try { setD(await llamar({ accion: 'ver' })); }
    catch (e) { setErr(e.message || 'No se pudo cargar tu plan.'); }
    finally { setCargando(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const confirmar = async () => {
    if (!pendiente) return;
    setBusy(true); setErr('');
    try {
      const res = await llamar(pendiente.cuerpo);
      setPendiente(null);
      // El portal dice QUÉ pasó (`nota_tipo`); aquí se cuenta con la moneda del sistema.
      const m = Math.abs(Number(res.monto) || 0);
      const que = {
        prueba: 'Sin cargo: estás en período de prueba.',
        cargo: `Se suma ${fmt$(m)} a tu próxima factura.`,
        credito: `Se te acredita ${fmt$(m)} en tu próxima factura.`,
        sin_credito: 'Este mes se factura completo; la mensualidad menor empieza en la próxima factura.',
        ya_facturado: 'Sin cargo adicional: este mes ya se factura con una mensualidad igual o mayor.',
      }[res.nota_tipo] || '';
      setAviso(`${pendiente.hecho} ${que}`.trim());
      await onCambio?.();          // la app recarga su configuración: los módulos nuevos aparecen ya
      await cargar();
    } catch (e) { setErr(e.message || 'No se pudo completar el cambio.'); setPendiente(null); }
    finally { setBusy(false); }
  };

  if (cargando) return <div className="text-silver" style={{ padding: 30, textAlign: 'center' }}>Cargando tu plan…</div>;

  const caja = { background: 'rgba(var(--sky-rgb),0.06)', border: '1px solid rgba(var(--sky-rgb),0.2)', borderRadius: 12, padding: '18px 20px' };
  const chip = (activo) => ({
    padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    color: activo ? 'var(--text-success)' : 'var(--silver)',
    background: activo ? 'rgba(34,197,94,0.1)' : 'rgba(var(--silver-rgb),0.08)',
    border: `1px solid ${activo ? 'rgba(34,197,94,0.3)' : 'rgba(var(--silver-rgb),0.2)'}`,
  });

  // ── Sin conexión al autoservicio: se muestra lo que la propia instancia sabe ──
  if (!d?.conectado) {
    const mods = Array.isArray(cfg?.modulos_activos) ? cfg.modulos_activos : [];
    const tope = Number(cfg?.limite_usuarios) || 0;
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        {err && <div style={{ color: 'var(--text-danger)', fontSize: 13 }}>{err}</div>}
        <div style={caja}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sky-light)', letterSpacing: 1 }}>TU PLAN</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: '4px 0 10px' }}>{cfg?.plan || 'Sin plan configurado'}</div>
          <div className="text-silver" style={{ fontSize: 13, marginBottom: 12 }}>
            <Users size={13} style={{ verticalAlign: '-2px' }} /> {activosLocal} usuario(s) activos{tope > 0 ? ` de ${tope}` : ' · sin tope'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(modulosCatalogo || []).filter(m => !SIEMPRE.includes(m.key) && m.key !== 'pagos').map(m => {
              const activo = !mods.length || mods.includes(m.key);
              return <span key={m.key} style={{ ...chip(activo), opacity: activo ? 1 : .65 }}>{activo ? '✓' : '·'} {m.label}</span>;
            })}
          </div>
        </div>
        <div className="text-silver" style={{ fontSize: 12 }}>El cambio de plan desde aquí aún no está activado para tu sistema.</div>
      </div>
    );
  }

  const a = d.actual, hoy = d.hoy;
  const activos = d.usuarios_activos ?? activosLocal;
  const modsActual = (a.modulos || []).filter(m => !SIEMPRE.includes(m));
  const otros = (d.planes || []).filter(p => p.id !== a.plan_id);

  // Vista previa del cargo: MISMA regla que el portal (que es quien manda y devuelve el monto real).
  const previa = (p) => {
    const sube = Number(p.precio_mensual) >= Number(a.precio_mensual);
    let extras = a.usuarios_extra || 0;
    if (sube && !a.tope_especial && !a.ilimitado) {
      extras = Math.max(0, Math.min(extras, (a.limite_base || 0) + extras - (Number(p.limite_usuarios) || 0)));
    }
    const despues = r2(Number(p.precio_mensual) + extras * (Number(p.precio_usuario_extra ?? 9)));
    const dif = r2(despues - a.mensualidad);
    const mods = (p.modulos || []).filter(m => !SIEMPRE.includes(m));
    const pierdeSinCredito = (d.modulos_sin_credito || []).filter(m => modsActual.includes(m) && !mods.includes(m));
    // Si este mes ya se bajó de plan SIN crédito, el mes se factura completo a esa
    // mensualidad (el "techo"): volver a subir solo paga lo que pase de ahí.
    const techo = Number(d.techo_mes) || 0;
    let monto = 0;
    if (!d.cliente.en_prueba) {
      if (dif >= 0) monto = r2(Math.max(0, despues - Math.max(a.mensualidad, techo)) * hoy.factor);
      else if (!pierdeSinCredito.length) monto = r2(dif * hoy.factor);
    }
    return {
      sube, extras, despues, dif, monto, pierdeSinCredito, yaFacturado: dif >= 0 && techo > a.mensualidad,
      gana: mods.filter(m => !modsActual.includes(m)),
      pierde: modsActual.filter(m => !mods.includes(m)),
      tope: a.tope_especial ? a.limite_usuarios : (Number(p.limite_usuarios) || 0) + extras,
    };
  };

  const pedirPlan = (p) => {
    const v = previa(p);
    const lineas = [
      `Pasas de ${a.nombre || 'sin plan'} (${fmt$(a.mensualidad)} al mes) a ${p.nombre} (${fmt$(v.despues)} al mes). El cambio es inmediato.`,
    ];
    if (v.extras !== (a.usuarios_extra || 0)) lineas.push(`Tus usuarios extra pasan de ${a.usuarios_extra} a ${v.extras}: el plan ${p.nombre} ya los incluye, así no pagas de más.`);
    if (v.pierde.length) lineas.push(`Dejarás de ver: ${v.pierde.map(etiqueta).join(', ')}. Tu información no se borra; si vuelves a subir de plan, reaparece.`);
    if (d.cliente.en_prueba) lineas.push('Estás en período de prueba: este cambio no tiene cargo.');
    else if (v.dif >= 0 && v.monto <= 0) lineas.push(`Sin cargo adicional este mes: ya se factura completo a ${fmt$(d.techo_mes)}. Desde el próximo mes pagas ${fmt$(v.despues)}.`);
    else if (v.yaFacturado) lineas.push(`En tu próxima factura se suma ${fmt$(v.monto)}: solo lo que pasa de ${fmt$(d.techo_mes)}, que este mes ya se factura completo, por los ${hoy.restantes} días que quedan (de ${hoy.diasMes}).`);
    else if (v.dif >= 0) lineas.push(`En tu próxima factura se suma ${fmt$(v.monto)}: la diferencia de este mes por los ${hoy.restantes} días que quedan (de ${hoy.diasMes}).`);
    else if (v.pierdeSinCredito.length) lineas.push(`Este mes se factura completo porque tu plan actual incluye ${v.pierdeSinCredito.map(etiqueta).join(', ')}. La mensualidad menor empieza en la próxima factura.`);
    else lineas.push(`En tu próxima factura se te acredita ${fmt$(-v.monto)} por los ${hoy.restantes} días que quedan de este mes (de ${hoy.diasMes}).`);
    setAviso(''); setErr('');
    setPendiente({ titulo: `Cambiar a plan ${p.nombre}`, lineas, boton: `Confirmar cambio a ${p.nombre}`, hecho: `Listo: ya estás en el plan ${p.nombre}.`, cuerpo: { accion: 'cambiar_plan', plan_id: p.id } });
  };

  const pedirExtra = (delta) => {
    const monto = d.cliente.en_prueba ? 0 : r2(delta * a.precio_usuario_extra * hoy.factor);
    const lineas = delta > 0
      ? [`Agregas 1 usuario extra por ${fmt$(a.precio_usuario_extra)} al mes. Tu tope pasa de ${a.limite_usuarios} a ${a.limite_usuarios + 1} usuarios, de inmediato.`,
         d.cliente.en_prueba ? 'Estás en período de prueba: no tiene cargo.' : `En tu próxima factura se suma ${fmt$(monto)} por los ${hoy.restantes} días que quedan de este mes; después, ${fmt$(a.precio_usuario_extra)} cada mes.`]
      : [`Quitas 1 usuario extra. Tu tope pasa de ${a.limite_usuarios} a ${a.limite_usuarios - 1} usuarios.`,
         d.cliente.en_prueba ? 'Estás en período de prueba: no tiene cargo.' : `En tu próxima factura se te acredita ${fmt$(-monto)} por los días que quedan de este mes.`];
    setAviso(''); setErr('');
    setPendiente({ titulo: delta > 0 ? 'Agregar un usuario extra' : 'Quitar un usuario extra', lineas, boton: delta > 0 ? 'Confirmar usuario extra' : 'Confirmar', hecho: delta > 0 ? 'Listo: ya puedes crear un usuario más.' : 'Listo: se quitó el usuario extra.', cuerpo: { accion: 'usuario_extra', delta } });
  };

  // `fmt` da formato a los números (el espacio va en MB/GB); `avisoDesde` pinta de alerta
  // antes de llenarse (el espacio avisa al 80%: nunca debe tomar al cliente por sorpresa).
  const medidor = (titulo, usados, limite, { fmt = v => v, avisoDesde = 1 } = {}) => {
    const pct = limite ? Math.min(100, Math.round((usados / limite) * 100)) : 0;
    const lleno = limite && usados >= limite * avisoDesde;
    return (
      <div style={{ flex: '1 1 220px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
          <span className="text-silver">{titulo}</span>
          <span style={{ fontWeight: 700, color: lleno ? 'var(--text-warning)' : 'var(--text)' }}>{fmt(usados)}{limite ? ` de ${fmt(limite)}` : ' · sin tope'}</span>
        </div>
        {!!limite && (
          <div style={{ height: 6, borderRadius: 4, background: 'rgba(var(--silver-rgb),0.2)', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: lleno ? '#f59e0b' : 'var(--sky)' }} />
          </div>
        )}
      </div>
    );
  };

  const totalMov = r2((d.movimientos || []).reduce((s, m) => s + (Number(m.monto) || 0), 0));
  // Estimado del próximo estado de cuenta: mensualidad + lo aceptado y aún no cobrado + impuesto.
  const subEstimado = r2(a.mensualidad + totalMov);
  const impPct = Number(d.impuesto?.pct) || 0;
  const impEstimado = r2(Math.max(0, subEstimado) * impPct / 100);

  // El documento lo arma el portal (una sola plantilla): aquí solo se imprime. En el diálogo
  // del navegador se elige "Guardar como PDF".
  const descargar = async (ec) => {
    setErr(''); setBajando(ec.id);
    try {
      const res = await llamar({ accion: 'estado_cuenta', id: ec.id });
      imprimirHTML(res.html, (m) => setErr(m.replace('No se pudo imprimir', 'No se pudo abrir el documento')));
    } catch (e) { setErr(e.message || 'No se pudo descargar el estado de cuenta.'); }
    finally { setBajando(null); }
  };

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {aviso && (
        <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, color: 'var(--text-success)', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }}>
          <CheckCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{aviso}
        </div>
      )}
      {err && <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, color: 'var(--text-danger)', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)' }}>{err}</div>}

      {/* ── Tu plan hoy ── */}
      <div style={caja}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sky-light)', letterSpacing: 1 }}>TU PLAN{d.cliente.en_prueba ? ' · PERÍODO DE PRUEBA' : ''}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', marginTop: 2 }}>{a.nombre || 'Sin plan'}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>{fmt$(a.mensualidad)}<span className="text-silver" style={{ fontSize: 13, fontWeight: 500 }}> al mes</span></div>
            {a.usuarios_extra > 0 && <div className="text-silver" style={{ fontSize: 12 }}>{fmt$(a.precio_mensual)} del plan + {a.usuarios_extra} usuario(s) extra</div>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '16px 0' }}>
          {medidor('Usuarios activos', activos, a.ilimitado ? 0 : a.limite_usuarios)}
          {d.ia && medidor('Análisis con IA este mes', d.ia.usados, d.ia.limite || 0)}
          {d.correos && medidor('Correos este mes', d.correos.usados, d.correos.limite || 0, { avisoDesde: 0.8 })}
          {d.espacio && medidor('Espacio de documentos', d.espacio.archivos_bytes / GB, Number(d.espacio.limite_gb) || 0, { fmt: fmtEspacio, avisoDesde: 0.8 })}
        </div>
        {d.espacio?.limite_gb > 0 && d.espacio.archivos_bytes / GB >= d.espacio.limite_gb * 0.8 && (
          <div style={{ fontSize: 12, color: 'var(--text-warning)', margin: '-6px 0 14px' }}>
            {d.espacio.archivos_bytes / GB >= d.espacio.limite_gb
              ? 'Llegaste al espacio de documentos de tu plan. Tus documentos siguen disponibles; un plan mayor incluye más espacio.'
              : 'Estás cerca del espacio de documentos de tu plan. Un plan mayor incluye más espacio.'}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {modsActual.map(m => <span key={m} style={chip(true)}>✓ {etiqueta(m)}</span>)}
        </div>

        {!a.ilimitado && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(var(--sky-rgb),0.15)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="text-silver" style={{ fontSize: 13, flex: '1 1 260px' }}>
              ¿Necesitas un usuario más sin cambiar de plan? Cada usuario extra cuesta {fmt$(a.precio_usuario_extra)} al mes.
            </span>
            {a.usuarios_extra > 0 && <button className="btn btn-sm btn-secondary" onClick={() => pedirExtra(-1)}>Quitar un extra</button>}
            <button className="btn btn-sm btn-primary" onClick={() => pedirExtra(1)}><Plus size={13} /> Agregar usuario extra</button>
          </div>
        )}
      </div>

      {/* ── Los otros planes, por lo que SUMAN ── */}
      {otros.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>Otros planes</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {otros.map(p => {
              const v = previa(p);
              const difUsuarios = (Number(p.limite_usuarios) || 0) - (a.limite_base || 0);
              const difIA = (Number(p.limite_ia_mes) || 0) - ((d.planes.find(x => x.id === a.plan_id)?.limite_ia_mes) || 0);
              const difGB = (Number(p.limite_gb) || 0) - ((d.planes.find(x => x.id === a.plan_id)?.limite_gb) || 0);
              return (
                <div key={p.id} style={{ flex: '1 1 260px', border: '1px solid rgba(var(--silver-rgb),0.25)', borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>{p.nombre}</div>
                    <div style={{ fontWeight: 700, color: 'var(--text)' }}>{fmt$(p.precio_mensual)}<span className="text-silver" style={{ fontSize: 12, fontWeight: 500 }}> al mes</span></div>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--silver)', lineHeight: 1.6, flex: 1 }}>
                    {v.sube ? (
                      <>
                        {v.gana.length > 0 && <div><strong style={{ color: 'var(--text-success)' }}>Sumas:</strong> {v.gana.map(etiqueta).join(', ')}</div>}
                        {!a.tope_especial && difUsuarios > 0 && <div>{difUsuarios} usuario(s) más incluidos ({p.limite_usuarios} en total)</div>}
                        {difIA > 0 && <div>{difIA} análisis con IA más cada mes ({p.limite_ia_mes})</div>}
                        {difGB > 0 && <div>{difGB} GB más para documentos ({p.limite_gb} GB)</div>}
                      </>
                    ) : (
                      <>
                        <div>Incluye {p.limite_usuarios} usuario(s), {p.limite_ia_mes ?? '—'} análisis con IA al mes{p.limite_gb ? ` y ${p.limite_gb} GB para documentos` : ''}.</div>
                        {v.pierde.length > 0 && <div>No incluye: {v.pierde.map(etiqueta).join(', ')}</div>}
                      </>
                    )}
                  </div>
                  <button className={`btn btn-sm ${v.sube ? 'btn-primary' : 'btn-secondary'}`} onClick={() => pedirPlan(p)}>
                    <RefreshCw size={13} /> Cambiar a {p.nombre}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Lo que ya aceptaste este mes ── */}
      <div style={{ border: '1px solid rgba(var(--silver-rgb),0.2)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Lo que va para tu próximo estado de cuenta</div>
        <div className="text-silver" style={{ fontSize: 12, marginBottom: 12 }}>Tu mensualidad (se cobra por adelantado) más lo que has aceptado y aún no se te cobra. Nada entra aquí sin que lo hayas confirmado.</div>
        <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text)' }}>
            <span>Mensualidad — plan {a.nombre || '—'}{a.usuarios_extra > 0 ? ` + ${a.usuarios_extra} usuario(s) extra` : ''}</span>
            <strong>{d.cliente.en_prueba ? 'Período de prueba' : fmt$(a.mensualidad)}</strong>
          </div>
          {(d.movimientos || []).map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--silver)' }}>
              <span>
                {m.resumen}
                {m.motivo && <span style={{ fontSize: 11, opacity: .8 }}> — {{ prueba: 'período de prueba', sin_credito: 'el mes se factura completo', ya_facturado: 'ya cubierto por lo facturado este mes' }[m.motivo]}</span>}
              </span>
              <strong style={{ color: m.monto < 0 ? 'var(--text-success)' : 'var(--text)', whiteSpace: 'nowrap' }}>{m.monto ? fmt$(m.monto) : 'sin cargo'}</strong>
            </div>
          ))}
          {!d.cliente.en_prueba && (
            <>
              {impPct > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid rgba(var(--silver-rgb),0.2)', color: 'var(--silver)' }}>
                  <span>{d.impuesto?.nombre || 'ITBMS'} {impPct}%</span><strong>{fmt$(impEstimado)}</strong>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: impPct > 0 ? 0 : 8, borderTop: impPct > 0 ? 'none' : '1px solid rgba(var(--silver-rgb),0.2)', color: 'var(--text)' }}>
                <span>Estimado</span><strong>{fmt$(r2(subEstimado + impEstimado))}</strong>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Sus estados de cuenta: lo que Corredata ya le emitió, con su PDF ── */}
      <div style={{ border: '1px solid rgba(var(--silver-rgb),0.2)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Tus estados de cuenta</div>
        {(d.estados_cuenta || []).length === 0 ? (
          <div className="text-silver" style={{ fontSize: 12 }}>
            {d.cliente.en_prueba ? 'Estás en período de prueba: todavía no se te cobra nada.' : 'Aún no tienes estados de cuenta. Cuando se emita el primero, aparecerá aquí con su PDF.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {d.estados_cuenta.map(ec => {
              const pagado = ec.estado === 'pagado';
              return (
                <div key={ec.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 10, background: 'rgba(var(--silver-rgb),0.06)' }}>
                  <div style={{ flex: '1 1 220px' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{ec.periodo_nombre}</div>
                    <div className="text-silver" style={{ fontSize: 12 }}>
                      {ec.numero}{ec.factura_fiscal_num ? ` · factura fiscal ${ec.factura_fiscal_num}` : ''}
                    </div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: pagado ? 'var(--text-success)' : 'var(--text-warning)' }}>
                    {pagado ? `Pagado el ${fechaCorta(ec.pagado_el)}` : `Por pagar · vence ${fechaCorta(ec.fecha_vencimiento)}`}
                  </span>
                  <strong style={{ color: 'var(--text)', minWidth: 90, textAlign: 'right' }}>{fmt$(ec.total)}</strong>
                  <button className="btn btn-sm btn-secondary" onClick={() => descargar(ec)} disabled={bajando === ec.id}>
                    {bajando === ec.id ? 'Abriendo…' : 'Descargar PDF'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Confirmación simple, como el paquete de IA ── */}
      {pendiente && (
        <div className="modal-overlay" onClick={() => !busy && setPendiente(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{pendiente.titulo}</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => !busy && setPendiente(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {pendiente.lineas.map((l, i) => <p key={i} style={{ fontSize: 13, color: i === 0 ? 'var(--text)' : 'var(--silver)', lineHeight: 1.6, margin: '0 0 10px' }}>{l}</p>)}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPendiente(null)} disabled={busy}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmar} disabled={busy}>{busy ? 'Aplicando…' : pendiente.boton}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
